import React, { useState } from 'react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        body: formDataToSend,
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: 'Message Sent!',
          description: `Thank you for contacting us. We'll get back to you within 24-48 hours.`,
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to send message. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try contacting us directly at studio@atlanticcoasttattoo.com',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-20 bg-black">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-display text-4xl md:text-6xl font-bold text-white mb-4">
            CONTACT US
          </h2>
          <p className="text-white/70 tracking-wider">
            GET IN TOUCH TO BOOK YOUR APPOINTMENT
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-white text-sm tracking-wider mb-2">
                  NAME *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/10 border border-white/20 text-white px-4 py-3 focus:outline-none focus:border-white/40 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-white text-sm tracking-wider mb-2">
                  EMAIL *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/10 border border-white/20 text-white px-4 py-3 focus:outline-none focus:border-white/40 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-white text-sm tracking-wider mb-2">
                  PHONE
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-white/10 border border-white/20 text-white px-4 py-3 focus:outline-none focus:border-white/40 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-white text-sm tracking-wider mb-2">
                  MESSAGE *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  placeholder="Please provide details about your tattoo idea: design description, size, placement, preferred dates, etc."
                  className="w-full bg-white/10 border border-white/20 text-white px-4 py-3 focus:outline-none focus:border-white/40 transition-colors placeholder-white/40"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-white text-black px-8 py-3 text-sm tracking-widest hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-white text-sm tracking-widest mb-4">STUDIO LOCATION</h3>
              <p className="text-white/80">
                18 Cliff Road<br />
                Newquay, Cornwall<br />
                TR7 1SG, United Kingdom
              </p>
            </div>
            <div>
              <h3 className="text-white text-sm tracking-widest mb-4">PHONE</h3>
              <p className="text-white/80">
                <a href="tel:01637498050" className="hover:text-white transition-colors">01637 498050</a><br />
                <a href="tel:07966061849" className="hover:text-white transition-colors">07966 061849</a>
              </p>
            </div>
            <div>
              <h3 className="text-white text-sm tracking-widest mb-4">EMAIL</h3>
              <p className="text-white/80">
                <a href="mailto:studio@atlanticcoasttattoo.com" className="hover:text-white transition-colors">
                  studio@atlanticcoasttattoo.com
                </a>
              </p>
            </div>
            <div>
              <h3 className="text-white text-sm tracking-widest mb-4">SOCIAL MEDIA</h3>
              <div className="flex gap-4">
                <a
                  href="https://www.facebook.com/atlanticcoasttattoo/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white transition-colors text-sm tracking-wider"
                >
                  FACEBOOK
                </a>
                <a
                  href="https://www.instagram.com/atlanticcoasttattoo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white transition-colors text-sm tracking-wider"
                >
                  INSTAGRAM
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;